import type { AppPage } from "../context/MonyawnContext.types";
import { getSeoForPage } from "./routes";
import {
  BRAND_DESCRIPTION,
  BRAND_NAME,
  BRAND_TAGLINE,
  DEFAULT_OG_IMAGE,
  FOUNDER_NAME,
  ORGANIZATION_LEGAL_NAME,
  ORGANIZATION_NAME,
  SITE_URL,
  SOCIAL_PROFILES,
  toAbsoluteUrl,
} from "./siteConfig";

export type JsonLdRecord = Record<string, unknown>;

function routeToHashPath(page: AppPage): string {
  if (page === "login") return "/";
  return `/#${page}`;
}

function buildBreadcrumb(page: AppPage): JsonLdRecord {
  const route = getSeoForPage(page);
  const items: Array<{ name: string; item: string }> = [{ name: "Home", item: SITE_URL }];

  if (page !== "login") {
    items.push({
      name: route.breadcrumbLabel,
      item: toAbsoluteUrl(route.canonicalPath),
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

function buildFaqSchema(page: AppPage): JsonLdRecord | null {
  const route = getSeoForPage(page);
  if (!route.faq || route.faq.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: route.faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

function buildWebPageSchema(page: AppPage): JsonLdRecord {
  const route = getSeoForPage(page);

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: route.title,
    description: route.description,
    url: toAbsoluteUrl(route.canonicalPath),
    isPartOf: {
      "@type": "WebSite",
      name: BRAND_NAME,
      url: SITE_URL,
    },
    about: {
      "@type": "Thing",
      name: BRAND_NAME,
      description: BRAND_DESCRIPTION,
    },
    primaryImageOfPage: DEFAULT_OG_IMAGE,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "main p"],
    },
    potentialAction: {
      "@type": "ViewAction",
      target: `${SITE_URL}${routeToHashPath(page)}`,
    },
  };
}

function buildOrganizationSchema(): JsonLdRecord {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORGANIZATION_NAME,
    legalName: ORGANIZATION_LEGAL_NAME,
    url: SITE_URL,
    logo: toAbsoluteUrl("/favicon.svg"),
    sameAs: SOCIAL_PROFILES,
    founder: {
      "@type": "Person",
      name: FOUNDER_NAME,
    },
  };
}

function buildWebSiteSchema(): JsonLdRecord {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND_NAME,
    alternateName: `${BRAND_NAME} Opportunity Management Platform`,
    url: SITE_URL,
    description: BRAND_DESCRIPTION,
    publisher: {
      "@type": "Organization",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

function buildSoftwareApplicationSchema(): JsonLdRecord {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: BRAND_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: SITE_URL,
    description: BRAND_DESCRIPTION,
    featureList: [
      "Local-first opportunity workspace",
      "Evidence-backed narrative generation",
      "Guided opportunity intake",
      "Exportable ZIP handoff packages",
      "Admin governance controls",
    ],
    creator: {
      "@type": "Organization",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
  };
}

function buildWebApplicationSchema(page: AppPage): JsonLdRecord {
  const route = getSeoForPage(page);
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: BRAND_NAME,
    url: toAbsoluteUrl(route.canonicalPath),
    browserRequirements: "Requires JavaScript. Works in modern desktop and mobile browsers.",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any",
    abstract: BRAND_TAGLINE,
    description: route.intro,
  };
}

export function buildSchemasForPage(page: AppPage): JsonLdRecord[] {
  const schemas: JsonLdRecord[] = [
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildSoftwareApplicationSchema(),
    buildWebApplicationSchema(page),
    buildWebPageSchema(page),
    buildBreadcrumb(page),
  ];

  const faq = buildFaqSchema(page);
  if (faq) schemas.push(faq);

  return schemas;
}
